import { Button } from '@/client/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { formatDate, getWeekRange } from '@/client/lib/dates';
import { isRangeCovered } from '@/client/lib/timesheets';
import { cn } from '@/client/lib/utils';
import { trpc } from '@/client/utils/trpc';
import type { RouterOutputs } from '@/client/utils/trpc';
import { Check, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { WeekNav } from './WeekNav';
import { WeekSummaryStrip } from './WeekSummaryStrip';

type AdminWeekStatusRow = RouterOutputs['timesheets']['adminWeekStatus'][number];

type ApprovalState = 'pending' | 'approved' | 'rejected';

interface AdminWeekPanelProps {
  employee: AdminWeekStatusRow | null;
  weekOffset: number;
  onOffsetChange: (n: number) => void;
}

export function AdminWeekPanel({ employee, weekOffset, onOffsetChange }: AdminWeekPanelProps) {
  const [approvalState, setApprovalState] = useState<ApprovalState>('pending');
  const [projectFilter, setProjectFilter] = useState('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: weekOffset is a prop, not a stable outer-scope value
  useEffect(() => {
    setApprovalState('pending');
    setProjectFilter('');
  }, [employee?.id, weekOffset]);

  const range = getWeekRange(weekOffset);

  const { data: entries = [], isLoading } = trpc.timesheets.listEntries.useQuery(
    { date_from: range.from, date_to: range.to, user_id: employee?.id },
    { enabled: employee !== null },
  );
  const { data: submissions = [] } = trpc.timesheets.listSubmissions.useQuery(
    { user_id: employee?.id },
    { enabled: employee !== null },
  );

  const projectOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of entries) seen.set(e.project_id, e.project_name);
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);

  const filteredEntries = projectFilter
    ? entries.filter((e) => e.project_id === projectFilter)
    : entries;

  const weekSubmitted = isRangeCovered(range.from, range.to, submissions);

  if (employee === null) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-sm text-muted-foreground">
          Select a team member to view their timesheet.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center gap-4 border-b px-6 py-3">
        <div className="flex-1">
          <p className="text-sm font-semibold">{employee.full_name ?? '—'}</p>
          {employee.job_title && (
            <p className="text-xs text-muted-foreground">{employee.job_title}</p>
          )}
        </div>
        {!isLoading && projectOptions.length > 0 && (
          <Select
            value={projectFilter || '_all'}
            onValueChange={(v) => setProjectFilter(v === '_all' ? '' : v)}
          >
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All projects</SelectItem>
              {projectOptions.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <WeekNav
          label={range.label}
          onPrev={() => onOffsetChange(weekOffset - 1)}
          onNext={() => onOffsetChange(weekOffset + 1)}
          onToday={() => onOffsetChange(0)}
          showToday={weekOffset !== 0}
        />
        {weekSubmitted && (
          <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-400">
            Submitted
          </span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        <WeekSummaryStrip
          entries={filteredEntries}
          weekFrom={range.from}
          weekSubmitted={weekSubmitted}
        />

        <div className="rounded-lg border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th scope="col" className="px-5 pb-2 pt-3 font-medium">Date</th>
                  <th scope="col" className="px-5 pb-2 pt-3 font-medium">Project</th>
                  <th scope="col" className="px-5 pb-2 pt-3 text-right font-medium">Hours</th>
                  <th scope="col" className="px-5 pb-2 pt-3 font-medium">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {isLoading && (
                  <tr>
                    <td colSpan={4} className="px-5 py-6 text-center text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                )}
                {!isLoading && filteredEntries.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-5 py-6 text-center text-muted-foreground">
                      No entries for this week.
                    </td>
                  </tr>
                )}
                {filteredEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td className="px-5 py-3 text-muted-foreground">{formatDate(entry.date)}</td>
                    <td className="px-5 py-3">{entry.project_name}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{entry.hours}</td>
                    <td className="px-5 py-3 text-muted-foreground">{entry.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="flex gap-2 border-t px-6 py-4">
        <Button
          type="button"
          variant={approvalState === 'approved' ? 'default' : 'outline'}
          size="sm"
          className="gap-1.5"
          onClick={() => setApprovalState((s) => (s === 'approved' ? 'pending' : 'approved'))}
        >
          <Check className="h-3.5 w-3.5" />
          {approvalState === 'approved' ? 'Approved' : 'Approve week'}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            'gap-1.5',
            approvalState === 'rejected' && 'border-destructive text-destructive',
          )}
          onClick={() => setApprovalState((s) => (s === 'rejected' ? 'pending' : 'rejected'))}
        >
          <X className="h-3.5 w-3.5" />
          {approvalState === 'rejected' ? 'Rejected' : 'Reject week'}
        </Button>
      </div>
    </div>
  );
}
